import React, { FunctionComponent, useCallback, useMemo, useState } from 'react'

import { skipToken } from '@reduxjs/toolkit/query'

import { CareTeamSlot as CareTeamFormSlot } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/careTeamSelectionForm/CareTeamSelectionForm'
import { ManageCareTeamOrganism } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/manageCareTeamOrganism/ManageCareTeamOrganism'

import { getManageCareTeamFormSlots } from './getManageCareTeamFormSlots'
import { getCareTeamChanges, getCareTeamSelections } from './hooks/careTeamChanges'
import { useCareTeamProviderProfile } from './hooks/useCareTeamProviderProfile'
import { useCareTeamSlotLabel } from './hooks/useCareTeamSlotLabel'
import { useSaveCareTeam } from './hooks/useSaveCareTeam'
import { useGetCareTeamSlotsQuery } from '../../../episodes/data/episodesApi'
import { useSelectedEpisodeForClient } from '../../../episodes/data/hooks/useSelectedEpisodeForClient'

type ManageCareTeamModalProps = {
  onRequestClose: () => void
}

export const ManageCareTeamModal: FunctionComponent<ManageCareTeamModalProps> = ({ onRequestClose }) => {
  const { data: selectedEpisode } = useSelectedEpisodeForClient()
  const { currentData: careTeam } = useGetCareTeamSlotsQuery(
    selectedEpisode ? { episodeId: selectedEpisode.id } : skipToken,
  )
  const { getProviderProfile } = useCareTeamProviderProfile()
  const getSlotLabel = useCareTeamSlotLabel()
  const { saveCareTeam, isSaving } = useSaveCareTeam(selectedEpisode?.id)
  const [selections, setSelections] = useState<Record<string, string>>({})

  const formSlots = useMemo(
    () =>
      getManageCareTeamFormSlots(
        careTeam?.slots ?? [],
        (lyraId) => getProviderProfile(lyraId).displayName,
        getSlotLabel,
      ),
    [careTeam?.slots, getProviderProfile, getSlotLabel],
  )

  const handleSlotChange = useCallback((slotId: string, value: string) => {
    setSelections((previous) => ({ ...previous, [slotId]: value }))
  }, [])

  const handleConfirm = useCallback(async () => {
    const changes = getCareTeamChanges(careTeam?.slots ?? [], getCareTeamSelections(careTeam?.slots ?? [], selections))
    const saved = await saveCareTeam(changes)
    if (saved) {
      onRequestClose()
    }
  }, [careTeam?.slots, selections, saveCareTeam, onRequestClose])

  const handleRequestClose = useCallback(() => {
    if (!isSaving) {
      onRequestClose()
    }
  }, [isSaving, onRequestClose])

  if (!careTeam) {
    return null
  }

  const withSelection = (slot: CareTeamFormSlot): CareTeamFormSlot => ({
    ...slot,
    selectedValue: selections[slot.id] ?? slot.selectedValue,
  })

  return (
    <ManageCareTeamOrganism
      visible
      neuropsychTeamSlots={formSlots.neuropsychTeamSlots.map(withSelection)}
      alternateNeuropsychTesterSlot={
        formSlots.alternateNeuropsychTesterSlot && withSelection(formSlots.alternateNeuropsychTesterSlot)
      }
      careTeamSlots={formSlots.careTeamSlots.map(withSelection)}
      additionalSupportSlots={formSlots.additionalSupportSlots.map(withSelection)}
      onSlotChange={handleSlotChange}
      onConfirm={handleConfirm}
      confirmLoading={isSaving}
      onRequestClose={handleRequestClose}
    />
  )
}
